import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, Users, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const DAYS_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAYS_BN = ["রবি", "সোম", "মঙ্গল", "বুধ", "বৃহ", "শুক্র", "শনি"];
const BAR_COLORS = ["#22c55e", "#0ea5e9", "#22c55e", "#0ea5e9", "#22c55e", "#0ea5e9", "#22c55e"];

const DietitianAnalytics = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const bn = language === "bn";
  const [loading, setLoading] = useState(true);
  const [totalConsultations, setTotalConsultations] = useState(0);
  const [totalPatients, setTotalPatients] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [chartData, setChartData] = useState<{ day: string; consultations: number }[]>([]);

  const loadData = useCallback(async () => {
    if (!user) return;
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    const [consRes, patsRes, walletRes] = await Promise.all([
      supabase.from("conversations").select("id", { count: "exact", head: true })
        .eq("practitioner_id", user.id).eq("practitioner_type", "dietitian"),
      supabase.from("conversations").select("patient_id, created_at")
        .eq("practitioner_id", user.id).eq("practitioner_type", "dietitian"),
      supabase.from("wallets").select("total_earned").eq("user_id", user.id).maybeSingle(),
    ]);

    setTotalConsultations(consRes.count ?? 0);
    const uniquePatients = new Set((patsRes.data ?? []).map((c: any) => c.patient_id));
    setTotalPatients(uniquePatients.size);
    setTotalEarnings(walletRes.data?.total_earned ?? 0);

    const counts: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
    (patsRes.data ?? [])
      .filter((c: any) => c.created_at >= sevenDaysAgo)
      .forEach((c: any) => {
        const day = new Date(c.created_at).getDay();
        counts[day] = (counts[day] ?? 0) + 1;
      });

    const today = new Date().getDay();
    const ordered = Array.from({ length: 7 }, (_, i) => {
      const dayIndex = (today - 6 + i + 7) % 7;
      return {
        day: bn ? DAYS_BN[dayIndex] : DAYS_EN[dayIndex],
        consultations: counts[dayIndex] ?? 0,
      };
    });
    setChartData(ordered);
    setLoading(false);
  }, [user, bn]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("dietitian-analytics-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "conversations", filter: `practitioner_id=eq.${user.id}` }, () => { loadData(); })
      .on("postgres_changes", { event: "*", schema: "public", table: "wallets", filter: `user_id=eq.${user.id}` }, () => { loadData(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, loadData]);

  const stats = [
    { icon: BarChart3, labelEn: "Total Sessions", labelBn: "মোট সেশন", value: totalConsultations, gradient: "bg-[#e4f1eb]" },
    { icon: Users, labelEn: "Total Patients", labelBn: "মোট রোগী", value: totalPatients, gradient: "bg-[#e4f1eb]" },
  ];

  const container = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
  const item = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border/40 rounded-xl px-3 py-2 shadow-md text-xs">
          <p className="font-bold text-foreground">{label}</p>
          <p className="text-primary font-extrabold">{payload[0].value} {bn ? "সেশন" : "sessions"}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div className="space-y-6" variants={container} initial="hidden" animate="show">
      <motion.div className="grid grid-cols-2 gap-4" variants={container}>
        {stats.map((s, i) => (
          <motion.div key={i} variants={item}>
            <Card className="bg-card/60 backdrop-blur-sm border-border/40 hover:border-primary/30 transition-all hover:shadow-md group">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${s.gradient} group-hover:scale-110 transition-transform`}>
                    <s.icon className="h-5 w-5 text-primary" />
                  </div>
                  {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                </div>
                <p className="text-2xl font-extrabold tracking-tight">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{bn ? s.labelBn : s.labelEn}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      <motion.div variants={item}>
        <Card className="bg-card/60 backdrop-blur-sm border-border/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                {bn ? "মোট আয়" : "Total Earnings"}
              </span>
              <p className="text-xl font-extrabold text-primary">৳{totalEarnings.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <motion.div variants={item}>
        <Card className="bg-card/60 backdrop-blur-sm border-border/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-extrabold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              {bn ? "৭ দিনের সেশন (লাইভ)" : "7-Day Sessions (Live)"}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            {loading ? (
              <div className="flex items-center justify-center h-[180px]">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData} barSize={28} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
                  <XAxis dataKey="day" tick={{ fontSize: 11, fontWeight: 700, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--muted)/0.3)", radius: 8 }} />
                  <Bar dataKey="consultations" radius={[6, 6, 0, 0]}>
                    {chartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} fillOpacity={0.85} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default DietitianAnalytics;
